import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../api/client";
import type { AbsenceView, PersonView, UserSettings } from "../api/types";
import { holidayName } from "../lib/holidays";
import { usePersistedState } from "../lib/persistent-state";
import {
  type DayAway,
  type YearMonth,
  WEEKDAY_EN,
  WEEKDAY_FR,
  buildDayTooltip,
  colourForType,
  dayCoverage,
  daysInMonth,
  isoDate,
  isWeekend,
  sameDay,
  shiftMonth,
  todayYM,
} from "../lib/calendar-utils";

export function CalendarPage() {
  const { t, i18n } = useTranslation();
  // View toggles persisted in localStorage — users hate having to re-tick the
  // same boxes every refresh. Month cursor + search stay ephemeral (usually
  // the user wants "today" on landing, and search is transient by nature).
  const [ym, setYm] = useState<YearMonth>(todayYM());
  const [search, setSearch] = useState("");
  const [onlyTodayAbsent, setOnlyTodayAbsent] = usePersistedState(
    "whosout:calendar:onlyTodayAbsent", false,
  );
  // Hide opt-out (visibility=nothing) rows from the grid — they're informative
  // ("this person exists but is private") but crowd the view.
  const [hideOptOut, setHideOptOut] = usePersistedState(
    "whosout:calendar:hideOptOut", false,
  );
  // Managers can flip this to see what a peer sees (no override, so their
  // reports' actual visibility choice — including opt-out — surfaces).
  const [asEmployee, setAsEmployee] = usePersistedState(
    "whosout:calendar:asEmployee", false,
  );
  // Sort: click a column header to sort by it; click again to flip direction.
  // Default = team asc then name asc (matches the original spec §3.4 order).
  const [sortKey, setSortKey] = usePersistedState<"name" | "team">(
    "whosout:calendar:sortKey", "team",
  );
  const [sortDir, setSortDir] = usePersistedState<"asc" | "desc">(
    "whosout:calendar:sortDir", "asc",
  );
  const toggleSort = (key: "name" | "team") => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const days = useMemo(() => daysInMonth(ym), [ym]);
  const start = isoDate(days[0]!);
  const end = isoDate(days[days.length - 1]!);

  const todayIso = isoDate(new Date());
  // Append `?as_employee=true` when the manager toggled the "view as employee"
  // switch. Include in the query keys so the cache differentiates the two views.
  const asEmpQ = asEmployee ? "?as_employee=true" : "";
  const asEmpQAmp = asEmployee ? "&as_employee=true" : "";
  const [peopleQ, absencesQ, todayQ, settingsQ] = useQueries({
    queries: [
      {
        queryKey: ["people", asEmployee] as const,
        queryFn: () => apiFetch<PersonView[]>(`/people${asEmpQ}`),
      },
      {
        queryKey: ["absences", start, end, asEmployee] as const,
        queryFn: () =>
          apiFetch<AbsenceView[]>(`/absences?start=${start}&end=${end}${asEmpQAmp}`),
      },
      {
        // Always fetch today's absences separately so the "today" strip stays
        // populated even when the user navigates the grid to a different month.
        // Key starts with "absences" so Settings.save's invalidateQueries
        // (["absences"] prefix) catches this too — audience changes must
        // refresh the strip, not just the grid.
        queryKey: ["absences", "today", todayIso, asEmployee] as const,
        queryFn: () =>
          apiFetch<AbsenceView[]>(
            `/absences?start=${todayIso}&end=${todayIso}${asEmpQAmp}`,
          ),
      },
      {
        queryKey: ["my-settings"] as const,
        queryFn: () => apiFetch<UserSettings>("/me/settings"),
      },
    ],
  });
  const isLoading = peopleQ.isLoading || absencesQ.isLoading;
  const error = peopleQ.error ?? absencesQ.error;
  const data = absencesQ.data;
  const people = peopleQ.data;
  const showTodaySummary = settingsQ.data?.show_today_summary ?? true;
  const todaysAbsences = useMemo(
    () =>
      [...(todayQ.data ?? [])].sort((a, b) =>
        a.display_name.localeCompare(b.display_name),
      ),
    [todayQ.data],
  );
  const todayAbsentOids = useMemo(
    () => new Set(todaysAbsences.map((a) => a.target_oid)),
    [todaysAbsences],
  );

  // Build one row per visible person, attach matching absences (possibly empty).
  // The /people endpoint already applies the visibility filter (same rules as
  // /absences) and the requester's audience scope (team/company/favourites),
  // so anyone returned here is allowed to be shown as a row.
  const rows = useMemo(() => {
    if (!people) return [];
    const byOid = new Map<string, AbsenceView[]>();
    for (const a of data ?? []) {
      const list = byOid.get(a.target_oid);
      if (list) list.push(a);
      else byOid.set(a.target_oid, [a]);
    }
    const q = search.trim().toLowerCase();
    return people
      .filter((p) => !hideOptOut || !p.is_opt_out)
      .filter((p) => !onlyTodayAbsent || todayAbsentOids.has(p.target_oid))
      .filter((p) => {
        if (!q) return true;
        return (
          p.display_name.toLowerCase().includes(q) ||
          (p.team ?? "").toLowerCase().includes(q)
        );
      })
      .map((p) => ({
        oid: p.target_oid,
        name: p.display_name,
        team: p.team,
        inBoond: p.in_boond,
        isOptOut: p.is_opt_out,
        isOptOutDefault: p.is_opt_out_default,
        absences: byOid.get(p.target_oid) ?? [],
      }))
      .sort((a, b) => {
        const dir = sortDir === "asc" ? 1 : -1;
        if (sortKey === "name") {
          // Name primary, team tiebreak (asc regardless of direction).
          return dir * a.name.localeCompare(b.name)
            || (a.team ?? "").localeCompare(b.team ?? "");
        }
        // sortKey === "team"
        return dir * (a.team ?? "").localeCompare(b.team ?? "")
          || a.name.localeCompare(b.name);
      });
  }, [people, data, search, onlyTodayAbsent, hideOptOut, todayAbsentOids, sortKey, sortDir]);

  // Who is away on each day of the displayed month, for the date-header
  // tooltip. Built from `rows` rather than the raw `data` list so the tooltip
  // always agrees with what the grid actually shows — search, "hide discreet"
  // and "today only" filters included. Opt-out people never contribute here:
  // the server sends them no absence rows at all, so `row.absences` is empty.
  const absentByDay = useMemo(() => {
    const m = new Map<string, DayAway[]>();
    for (const d of days) {
      const iso = isoDate(d);
      const list: DayAway[] = [];
      for (const row of rows) {
        const cov = dayCoverage(row.absences, iso);
        if (cov) {
          // One entry per person, with every leave type of the day, so someone
          // on a morning of CP and an afternoon of RTT appears once — as away
          // for the full day.
          list.push({
            name: row.name,
            type: cov.types.map((ty) => ty ?? null).every((ty) => ty === null)
              ? null
              : cov.types.map((ty) => ty ?? "?").join(" + "),
            half: !cov.full,
          });
        }
      }
      // Alphabetical, matching the "away today" strip — the grid's own order is
      // team-first by default, which reads oddly in a flat list.
      list.sort((x, y) => x.name.localeCompare(y.name));
      m.set(iso, list);
    }
    return m;
  }, [days, rows]);

  // Translated once per language change and handed to the (pure) tooltip
  // builder, so calendar-utils stays free of any i18n dependency.
  const tooltipLabels = useMemo(
    () => ({
      awayOnDay: (count: number) => t("calendar.awayOnDay", { count }),
      noneAway: t("calendar.noneAwayOnDay"),
      binary: t("calendar.absentBinary"),
      halfDay: t("calendar.halfDay"),
      andNMore: (count: number) => t("calendar.andNMore", { count }),
    }),
    [t],
  );

  const locale = i18n.language.startsWith("fr") ? "fr-FR" : "en-GB";
  const periodLabel = days[0]!.toLocaleDateString(locale, { month: "long", year: "numeric" });
  const weekdays = i18n.language.startsWith("fr") ? WEEKDAY_FR : WEEKDAY_EN;
  const today = new Date();

  const types = useMemo(() => {
    const set = new Set<string>();
    for (const a of data ?? []) if (a.leave_type) set.add(a.leave_type);
    return Array.from(set).sort();
  }, [data]);

  return (
    <section>
      {showTodaySummary && todaysAbsences.length > 0 && (
        <div className="today-strip" aria-label={t("calendar.todaySummary", { count: todaysAbsences.length })}>
          <span className="today-strip-label">{t("calendar.todaySummary", { count: todaysAbsences.length })}</span>
          <ul>
            {todaysAbsences.map((a) => (
              <li key={`${a.target_oid}-${a.start_date}`}>
                <span
                  className="today-swatch"
                  style={{ background: colourForType(a.leave_type) }}
                  aria-hidden="true"
                />
                <span className="today-name">{a.display_name}</span>
                <span className="today-type">
                  {a.leave_type ?? t("calendar.absentBinary")}
                  {a.is_half_day ? ` (${t("calendar.halfDay")})` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="calendar-controls">
        <button className="secondary" onClick={() => setYm(shiftMonth(ym, -1))}>
          ← {t("calendar.prev")}
        </button>
        <span className="period-label">{periodLabel}</span>
        <button className="secondary" onClick={() => setYm(shiftMonth(ym, 1))}>
          {t("calendar.next")} →
        </button>
        <button className="ghost" onClick={() => setYm(todayYM())}>
          {t("calendar.today")}
        </button>
        <input
          type="search"
          className="person-search"
          placeholder={t("calendar.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t("calendar.searchPlaceholder")}
        />
        <label className="preview-toggle" title={t("calendar.hideOptOutHint")}>
          <input
            type="checkbox"
            checked={hideOptOut}
            onChange={(e) => setHideOptOut(e.target.checked)}
          />
          {t("calendar.hideOptOut")}
        </label>
        <label className="preview-toggle" title={t("calendar.onlyTodayHint")}>
          <input
            type="checkbox"
            checked={onlyTodayAbsent}
            onChange={(e) => setOnlyTodayAbsent(e.target.checked)}
          />
          {t("calendar.onlyToday")}
        </label>
        <label className="preview-toggle" title={t("calendar.asEmployeeHint")}>
          <input
            type="checkbox"
            checked={asEmployee}
            onChange={(e) => setAsEmployee(e.target.checked)}
          />
          {t("calendar.asEmployee")}
        </label>
        <span style={{ flex: 1 }} />
      </div>

      {isLoading && <p>…</p>}
      {error && <p role="alert">{t("errors.generic")}</p>}

      {!isLoading && !error && (
        <>
          <div className="calendar-wrap">
            <div
              className="calendar-grid"
              style={{ ["--day-count" as string]: days.length }}
            >
              <button
                type="button"
                className={`name-head sortable ${sortKey === "name" ? "sort-active" : ""}`}
                onClick={() => toggleSort("name")}
                aria-sort={sortKey === "name" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                title={t("calendar.sortBy", { col: t("calendar.person") })}
              >
                {t("calendar.person")}
                {sortKey === "name" && <span className="sort-arrow">{sortDir === "asc" ? "▲" : "▼"}</span>}
              </button>
              <button
                type="button"
                className={`team-head sortable ${sortKey === "team" ? "sort-active" : ""}`}
                onClick={() => toggleSort("team")}
                aria-sort={sortKey === "team" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                title={t("calendar.sortBy", { col: t("calendar.team") })}
              >
                {t("calendar.team")}
                {sortKey === "team" && <span className="sort-arrow">{sortDir === "asc" ? "▲" : "▼"}</span>}
              </button>
              {days.map((d) => {
                const isToday = sameDay(d, today);
                const hol = holidayName(d);
                const classes = [
                  "day-head",
                  isWeekend(d) ? "weekend" : "",
                  hol ? "holiday" : "",
                  isToday ? "today" : "",
                ].filter(Boolean).join(" ");
                const base = d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
                const dayTitle = buildDayTooltip(
                  hol ? `${base} — ${hol}` : base,
                  absentByDay.get(isoDate(d)) ?? [],
                  tooltipLabels,
                );
                return (
                  <div
                    key={`h-${d.getDate()}`}
                    className={classes}
                    title={dayTitle}
                  >
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
                      <span style={{ fontSize: "0.65rem", opacity: 0.7 }}>{weekdays[d.getDay()]}</span>
                      <span>{d.getDate()}</span>
                    </div>
                  </div>
                );
              })}

              {rows.map((row) => (
                <RowFragment key={row.oid} name={row.name} team={row.team} inBoond={row.inBoond} isOptOut={row.isOptOut} isOptOutDefault={row.isOptOutDefault} days={days} absences={row.absences} today={today} locale={locale} t={t} />
              ))}
            </div>
          </div>

          {rows.length === 0 && <p className="empty-msg">{t("calendar.empty")}</p>}

          {(types.length > 0 || rows.some((r) => r.isOptOut)) && (
            <div className="legend" aria-label={t("calendar.legend")}>
              {types.map((typ) => (
                <span key={typ} className="legend-item">
                  <span className="legend-swatch" style={{ background: colourForType(typ) }} />
                  {typ}
                </span>
              ))}
              <span className="legend-item">
                <span className="legend-swatch" style={{ background: "var(--leave-binary)" }} />
                {t("calendar.absentBinary")}
              </span>
              {rows.some((r) => r.isOptOut && !r.isOptOutDefault) && (
                <span className="legend-item">
                  <span className="legend-swatch" style={{ background: "var(--leave-optout)" }} />
                  {t("calendar.optOut")}
                </span>
              )}
              {rows.some((r) => r.isOptOutDefault) && (
                <span className="legend-item">
                  <span
                    className="legend-swatch legend-swatch-optout-default"
                    style={{ background: "var(--leave-other)" }}
                  />
                  {t("calendar.optOutDefault")}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

type TFn = (k: string, opts?: Record<string, unknown>) => string;

function RowFragment({
  name, team, inBoond, isOptOut, isOptOutDefault, days, absences, today, locale, t,
}: {
  name: string;
  team: string | null;
  inBoond: boolean;
  isOptOut: boolean;
  isOptOutDefault: boolean;
  days: Date[];
  absences: AbsenceView[];
  today: Date;
  locale: string;
  t: TFn;
}) {
  const optOutTitle = isOptOutDefault ? t("calendar.optOutDefault") : t("calendar.optOut");
  const nameTitle = isOptOut
    ? `${name} — ${optOutTitle}`
    : inBoond ? name : `${name} — ${t("calendar.noBoondWarning")}`;
  const optClass = isOptOut ? (isOptOutDefault ? " opt-out default" : " opt-out") : "";
  return (
    <>
      <div className={`name-cell${optClass}`} title={nameTitle}>
        {!inBoond && !isOptOut && (
          <span className="boond-warn" aria-label={t("calendar.noBoondWarning")} title={t("calendar.noBoondWarning")}>
            ⚠
          </span>
        )}
        {name}
      </div>
      <div className={`team-cell${optClass}`} title={team ?? ""}>{team ?? "—"}</div>
      {days.map((d) => {
        const isoD = isoDate(d);
        const cov = dayCoverage(absences, isoD);
        const colour = cov ? colourForType(cov.absences[0]!.leave_type) : "transparent";
        // Boond doesn't expose AM-vs-PM for half-days — colour only the AM half
        // as a visual "partial day" indicator. The tooltip clarifies. Two
        // half-days booked on the same date DO cover the whole day, so they get
        // a full cell: `cov.full` already accounts for that.
        const cellAm = colour;
        const cellPm = cov && !cov.full ? "transparent" : colour;
        const hol = holidayName(d);
        const cls = [
          "day-cell",
          isWeekend(d) ? "weekend" : "",
          hol ? "holiday" : "",
          sameDay(d, today) ? "today" : "",
        ].filter(Boolean).join(" ");
        const datePart = d.toLocaleDateString(locale, { day: "numeric", month: "long" });
        // Several leave types can share one day (a morning of CP plus an
        // afternoon of RTT): list them all rather than only the first.
        const typePart = cov
          ? cov.types.map((ty) => ty ?? t("calendar.absentBinary")).join(" + ")
          : "";
        const halfPart = cov && !cov.full ? ` (${t("calendar.halfDay")})` : "";
        const title = cov
          ? `${name} — ${typePart}${halfPart} — ${datePart}${hol ? ` (${hol})` : ""}`
          : hol
            ? `${datePart} — ${hol}`
            : "";
        return (
          <div
            key={isoD}
            className={cls}
            style={cov ? { ["--cell-am" as string]: cellAm, ["--cell-pm" as string]: cellPm } : undefined}
            title={title}
          />
        );
      })}
    </>
  );
}
